import {
  autocompletion,
  closeBrackets,
  closeCompletion,
  Completion,
} from "@codemirror/autocomplete";
import {
  defaultKeymap,
  history,
  historyKeymap,
  indentMore,
  indentSelection,
} from "@codemirror/commands";
import { javascript } from "@codemirror/lang-javascript";
import {
  bracketMatching,
  HighlightStyle,
  indentUnit,
  syntaxHighlighting,
  syntaxTree,
} from "@codemirror/language";
import {
  ChangeSet,
  Compartment,
  EditorSelection,
  EditorState,
  StateEffect,
  Transaction,
} from "@codemirror/state";
import {
  Command,
  Decoration,
  DecorationSet,
  drawSelection,
  EditorView,
  keymap,
  placeholder as cmPlaceholder,
  tooltips,
  ViewPlugin,
  ViewUpdate,
} from "@codemirror/view";
import { tags } from "@lezer/highlight";
import classNames from "classnames";
import { EventEmitter } from "eventemitter3";
import {
  action,
  computed,
  IComputedValue,
  IObservableValue,
  observable,
  reaction,
  runInAction,
} from "mobx";
import { observer } from "mobx-react-lite";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import type { CompletionEntryDetails, TextSpan } from "typescript";
import interact from "../lib/codemirror-interact";
import { appSettingsMobx } from "../SharedIframeState";
import {
  ActionType,
  EvaluateExpressionType,
  Pane,
  PaneExpression,
  PaneType,
  Rect,
} from "../Types";
import { FeatherIcon } from "../ui/FeatherIcon";
import {
  generatePaneInputId,
  getParentByClassName,
  isModifierKeyDown,
  isSecondaryModifierKeyDown,
  modifierKey,
} from "../Utils";
import { canvasDispatch } from "./CanvasDispatch";
import { canvasAtom, isShowingNonFocusedPopoverBox } from "./CanvasState";
import { EvaluatorRunEmitter } from "./CanvasStateEffects";
import {
  convertPanesToTypeScriptCode,
  createEvalPaneFunction,
  PANE_INPUT_MARKER,
  PANE_MARKER,
} from "./CodeAnalysis";
import {
  presenceDispatchPlugin,
  subscribeCodemirrorPaneId,
} from "./CodeMirrorPresence";
import { formatCodeWithCursor } from "./CodeOutput";
import { toggleCommandBar } from "./CommandBar";
import { LogInspectorValues } from "./LogInspectorValues";
import { dragValueAtom, onDragNewPaneMouseDown } from "./NewPane";
import {
  checkAndConfirmPushPaneInput,
  getPaneInputAutoName,
} from "./SandboxUtils";
import { applicationState } from "./Sync";

const LOG_VALUE_TOKEN_CLASSNAME = "cm-logValue";
const LOG_VALUE_SELECTED_TOKEN_CLASSNAME = "cm-logValueSelected";

let codeWorkerNextRequestId = 1;
const codeWorker = new Worker(new URL("./CodeWorker", import.meta.url), {
  type: "module",
});
export async function codeWorkerCall(
  request: Record<string, any>
): Promise<any | undefined> {
  const requestId = codeWorkerNextRequestId++;
  let rv;
  let resolveValue: Function;
  function onMessage(e: MessageEvent) {
    const data = e.data;
    if (data.req === requestId) {
      rv = data;
      resolveValue();
    }
  }
  await Promise.race([
    new Promise((resolve) => {
      resolveValue = resolve;
      codeWorker.addEventListener("message", onMessage);
      codeWorker.postMessage({ ...request, req: requestId });
    }),
    new Promise<void>((resolve) => setTimeout(() => resolve(), 3000)),
  ]);
  codeWorker.removeEventListener("message", onMessage);
  return rv;
}

export const PaneEditorRemoteUpdateEmitter = new EventEmitter();

const insertTab: Command = ({ state, dispatch }) => {
  if (state.selection.ranges.some((r) => !r.empty))
    return indentMore({ state, dispatch });
  dispatch(
    state.update(state.replaceSelection(state.facet(indentUnit)), {
      scrollIntoView: true,
      annotations: Transaction.userEvent.of("input"),
    })
  );
  return true;
};

async function formatEditorCode(editorView: EditorView) {
  const originalCode = editorView.state.doc.toString();
  let cursorOffset = editorView.state.selection.asSingle().ranges[0].from;

  let code = originalCode;
  // @ts-ignore
  const expressionType = editorView.expressionType;
  const shouldWrapWithReturn =
    expressionType === EvaluateExpressionType.Expression &&
    code.trim().startsWith("{");
  if (shouldWrapWithReturn) {
    code = `${RETURN_PREFIX}${code}`;
    cursorOffset += RETURN_PREFIX.length;
  }
  try {
    let { formatted, cursorOffset: newCursorOffset } =
      await formatCodeWithCursor(code, cursorOffset);
    // hack until the following issue is fixed
    // TODO: track https://github.com/vitejs/vite/issues/9191
    formatted = formatted.replaceAll(/^\t/gm, "");
    if (shouldWrapWithReturn) {
      formatted = formatted.substring(RETURN_PREFIX.length);
      newCursorOffset -= RETURN_PREFIX.length;
    }
    if (formatted.endsWith("\n")) {
      formatted = formatted.substring(0, formatted.length - 1);
    }
    if (
      expressionType === EvaluateExpressionType.Expression &&
      formatted.endsWith(";")
    ) {
      formatted = formatted.substring(0, formatted.length - 1);
    }
    if (originalCode === formatted) {
      return;
    }
    if (newCursorOffset > formatted.length) {
      newCursorOffset = formatted.length;
    }
    editorView.dispatch({
      changes: {
        from: 0,
        to: originalCode.length,
        insert: formatted,
      },
      selection: EditorSelection.cursor(newCursorOffset),
    });
  } catch {}
}

function wrapSelectionInCanvasPortal(
  editorView: EditorView,
  paneRect: Rect,
  libraryInputName: string
) {
  const selectionRange = editorView.state.selection.asSingle().ranges[0];

  editorView.dispatch({
    changes: [
      {
        from: selectionRange.from,
        insert: `<${libraryInputName}.CanvasPortal x={${paneRect[0]}} y={${
          // place above pane (-32) so content is visible
          paneRect[1] - 32
        }}>`,
      },
      {
        from: selectionRange.to,
        insert: `</${libraryInputName}.CanvasPortal>`,
      },
    ],
  });
}

function extractPaneName(editorView: EditorView): string | undefined {
  const expression = editorView.state.doc.toString();
  const matches = /^([a-zA-Z_$][0-9a-zA-Z_$]*)\s*=(?![=>])\s*/.exec(expression);
  if (matches !== null) {
    const [fullMatch, paneName] = matches;
    editorView.dispatch({
      changes: {
        from: 0,
        to: fullMatch.length,
      },
    });
    return paneName;
  }
}

async function renameInput(
  editorView: EditorView,
  paneId: string,
  paneInputId: string,
  newInputName: string
) {
  const pane = canvasAtom.value.panes.find((p) => p.id === paneId);
  if (pane === undefined || pane.type !== PaneType.Evaluate) {
    return;
  }
  const doc = editorView.state.doc.toString();
  const code = createEvalPaneFunction(pane, {
    allowUndefinedSource: true,
    overrideExpression: doc,
  });
  const inputNeedle = `/*${PANE_INPUT_MARKER(paneInputId)}*/`;
  const inputNameOffset = code.indexOf(inputNeedle) + inputNeedle.length;
  const codeNeedle = `/*${PANE_MARKER(paneId)}*/`;
  const expressionOffset = code.indexOf(codeNeedle) + codeNeedle.length;
  const response: { renameSpans: TextSpan[] } | undefined =
    await codeWorkerCall({
      type: "FindRenameSpans",
      code,
      pos: inputNameOffset,
    });
  if (response === undefined || editorView.state.doc.toString() !== doc) {
    return;
  }
  const renameSpans = response.renameSpans
    .map((span) => ({
      start: span.start - expressionOffset,
      length: span.length,
    }))
    .filter((span) => span.start >= 0);
  if (renameSpans.length > 0) {
    editorView.dispatch({
      changes: renameSpans.map((span) => ({
        from: span.start,
        to: span.start + span.length,
        insert: newInputName,
      })),
    });
  }
}

const setLogValuesEffect = StateEffect.define<
  [from: number, to: number, name: string, values: any[]][] | undefined
>();
const logValuesPlugin = ViewPlugin.fromClass(
  class {
    logValues:
      | [from: number, to: number, name: string, values: any[]][]
      | undefined;
    decorations: DecorationSet;

    constructor(view: EditorView) {
      this.logValues = undefined;
      this.decorations = Decoration.none;
    }

    update(update: ViewUpdate) {
      let shouldRecalculateDecorations = false;
      for (const tr of update.transactions) {
        for (let e of tr.effects) {
          if (e.is(setLogValuesEffect)) {
            this.logValues = e.value;
            shouldRecalculateDecorations = true;
          }
        }
        if (tr.docChanged) {
          this.logValues = undefined;
          shouldRecalculateDecorations = true;
        }
        if (tr.selection !== undefined) {
          shouldRecalculateDecorations = true;
        }
      }

      if (shouldRecalculateDecorations) {
        if (this.logValues === undefined) {
          this.decorations = Decoration.none;
        } else {
          const selection = update.state.selection;
          const selectionIndex =
            selection.ranges.length === 1 && selection.ranges[0].empty
              ? selection.ranges[0].anchor
              : undefined;
          this.decorations = Decoration.set(
            this.logValues.map(([from, to, values]) =>
              Decoration.mark({
                class: classNames(LOG_VALUE_TOKEN_CLASSNAME, {
                  [LOG_VALUE_SELECTED_TOKEN_CLASSNAME]:
                    selectionIndex !== undefined &&
                    from <= selectionIndex &&
                    selectionIndex <= to,
                }),
                attributes: {
                  "data-from": `${from}`,
                  "data-to": `${to}`,
                },
              }).range(from, to)
            )
          );
        }
      }
    }
  },
  {
    decorations: (v) => v.decorations,
    eventHandlers: {
      mousedown(e: MouseEvent, view: EditorView) {
        if (
          isModifierKeyDown(e.metaKey, e.ctrlKey) &&
          e.target instanceof HTMLElement
        ) {
          const logValueElement = getParentByClassName(
            e.target,
            LOG_VALUE_TOKEN_CLASSNAME
          );
          if (logValueElement !== undefined) {
            // This plugin is a hack to stop codemirror from processing mousedown
            // It's easier for me to implement in useEffect
            return true;
          }
        }
      },
    },
  }
);

const RETURN_PREFIX = "return ";
const languageCompartment = new Compartment();
export const vimCompartment = new Compartment();
export const cmLanguageExtensions = [
  javascript({ jsx: true }),
  closeBrackets(),
  bracketMatching(),
  logValuesPlugin,
];
const CodemirrorHighlightStyle = HighlightStyle.define([
  {
    tag: tags.keyword,
    class: "cmt-keyword",
  },
  {
    tag: tags.variableName,
    class: "cmt-variableName",
  },
  {
    tag: tags.propertyName,
    class: "cmt-propertyName",
  },
  {
    tag: tags.comment,
    class: "cmt-comment",
  },
  {
    tag: tags.punctuation,
    class: "cmt-punctuation",
  },
  {
    tag: tags.string,
    class: "cmt-string",
  },
]);
export const cmBaseTheme = EditorView.theme({
  "&": { height: "100%" },
  ".cm-line": { padding: "0 8px", lineHeight: "16px" },
  "&.cm-editor.cm-focused": { outline: "none" },
  ".cm-content": {
    caretColor: "rgb(var(--color-neutral-600))",
    padding: "8px 0 7px",
  },
  "&.cm-focused .cm-fat-cursor": {
    background: "rgb(var(--color-primary-300))",
  },
  "&:not(.cm-focused) .cm-fat-cursor": { display: "none" },
  "& .cm-selectionBackground": {
    background: "rgb(var(--color-neutral-150))",
  },
  "&.cm-focused .cm-selectionBackground": {
    background: "rgb(var(--color-neutral-200))",
  },
  ".cm-cursor": {
    borderColor: "rgb(var(--color-neutral-700))",
  },
  ".cm-matchingBracket .cmt-punctuation": {
    color: "rgb(var(--color-neutral-600))",
  },
  ".cm-nonmatchingBracket .cmt-punctuation": { color: "#ef4444" },
  ".cm-placeholder": { color: "rgb(var(--color-neutral-300))" },
  ".cm-panels": {
    backgroundColor: "transparent",
    color: "rgb(var(--color-neutral-500))",
  },
  ".cm-panels-bottom": {
    borderTop: "1px solid rgb(var(--color-neutral-200))",
  },
  ".cm-vim-panel": {
    fontFamily: "JetBrains Mono !important",
    padding: "4px 8px",
  },
  ".cm-vim-panel span": {
    display: "flex",
    alignItems: "center",
  },
  ".cm-vim-panel input": {
    boxShadow: "none !important",
    flexGrow: 1,
  },
  ".cm-searchMatch": {
    backgroundColor: "rgb(var(--color-secondary-200))",
  },
});
export const cmExtensions = [
  cmBaseTheme,
  EditorView.theme({
    ".cm-scroller": { fontFamily: "JetBrains Mono" },
    ".cmt-keyword": { color: "rgb(var(--color-accent-600))" },
    ".cmt-variableName": { color: "rgb(var(--color-accent-500))" },
    ".cmt-comment": { color: "rgb(var(--color-neutral-400))" },
    ".cmt-punctuation": { color: "rgb(var(--color-neutral-400))" },
    ".cmt-string": { color: "rgb(var(--color-code-string))" },
    "&.cm-focused .cm-matchingBracket": { backgroundColor: "transparent" },
    "&.cm-focused .cm-nonmatchingBracket": { backgroundColor: "transparent" },
    ".cm-tooltip": {
      backgroundColor: "rgb(var(--color-neutral-50))",
      borderColor: "rgb(var(--color-neutral-100))",
      fontFamily: "JetBrains Mono",
    },
    ".cm-completionIcon": {
      opacity: 0.6,
      width: "1.4em",
    },
    ".cm-completionIcon-import:after": {
      content: `"→"`,
    },
    ".cm-completionIcon-property:after": {
      content: `"𝑥"`,
    },
    ".cm-tooltip.cm-tooltip-autocomplete > ul": {
      fontFamily: "JetBrains Mono",
    },
    ".cm-tooltip.cm-tooltip-autocomplete > ul > li[aria-selected]": {
      backgroundColor: "rgb(var(--color-primary-700))",
      color: "rgb(var(--color-neutral-100))",
    },
    ".cm-tooltip.cm-completionInfo": {
      marginTop: "-2px",
      padding: "6px",
    },
    ".cm-completionDetail": {
      opacity: 0.5,
    },
    [`.${LOG_VALUE_TOKEN_CLASSNAME}`]: {
      position: "relative",
      whiteSpace: "nowrap",
    },
    [`.${LOG_VALUE_TOKEN_CLASSNAME}::before`]: {
      content: `""`,
      backgroundColor: "rgb(var(--color-primary-50))",
      borderRadius: "2px",
      left: "-2px",
      right: "-2px",
      top: "0",
      bottom: "0",
      position: "absolute",
      zIndex: "-1",
    },
    [`.pane-focused & .${LOG_VALUE_SELECTED_TOKEN_CLASSNAME}::before`]: {
      backgroundColor: "rgb(var(--color-primary-500))",
    },
    [`.pane-focused & .${LOG_VALUE_SELECTED_TOKEN_CLASSNAME} .cmt-propertyName`]:
      { color: "#ffffff" },
    [`.pane-focused & .${LOG_VALUE_SELECTED_TOKEN_CLASSNAME} .cmt-variableName`]:
      { color: "#ffffff" },
  }),
  history(),
  keymap.of([
    ...defaultKeymap,
    ...historyKeymap,
    { key: "Tab", run: insertTab, shift: indentSelection },
    {
      key: "Escape",
      run: (editorView) => {
        if (isShowingNonFocusedPopoverBox.get()) {
          return false;
        }
        editorView.dom.parentElement!.focus();
        return true;
      },
    },
  ]),
  EditorView.lineWrapping,
  syntaxHighlighting(CodemirrorHighlightStyle, { fallback: true }),
  tooltips({ parent: document.body }),
  interact({
    rules: [
      {
        regexp: /-?\b\d+\.?\d*\b/g,
        cursor: "ew-resize",
        onDrag: (text, setText, e) => {
          const newVal = Number(text) + e.movementX;
          if (!isNaN(newVal)) {
            setText(newVal.toString());
          }
        },
      },
    ],
  }),
];
function createAutocompleteExtension(paneId: string) {
  return autocompletion({
    override: [
      async (context) => {
        const hasPeriodTokenBefore = context.tokenBefore(["."]) !== null;

        const paneMatchOptions: Completion[] = [];
        let replacementSpanLength = 0;
        if (!hasPeriodTokenBefore) {
          const paneNameQuery = context.matchBefore(/\w+/)?.text.toLowerCase();
          if (
            context.explicit ||
            (paneNameQuery !== undefined && paneNameQuery.length >= 2)
          ) {
            const currentPane = canvasAtom.value.panes.find(
              (p) => p.id === paneId
            );
            if (currentPane !== undefined && "inputs" in currentPane) {
              if (paneNameQuery !== undefined) {
                replacementSpanLength = paneNameQuery.length;
              }
              for (const p of canvasAtom.value.panes) {
                if (
                  p.id !== paneId &&
                  p.type !== PaneType.EvaluateGlobal &&
                  typeof p.name === "string" &&
                  (paneNameQuery === undefined ||
                    p.name?.toLowerCase().startsWith(paneNameQuery)) &&
                  currentPane.inputs.findIndex(
                    (input) => input.source?.[0] === p.id
                  ) === -1
                ) {
                  const paneAutoInputName = getPaneInputAutoName(
                    currentPane.inputs,
                    0,
                    p,
                    undefined
                  );
                  const infoContent =
                    p.type === PaneType.Evaluate
                      ? p.expression
                      : p.type === PaneType.InputText
                      ? p.text
                      : undefined;
                  paneMatchOptions.push({
                    label: p.name,
                    detail: "pane",
                    apply: (view, completion, from, to) => {
                      if (
                        checkAndConfirmPushPaneInput(
                          canvasAtom.value.panes,
                          currentPane.id,
                          p.id
                        )
                      ) {
                        canvasDispatch([
                          ActionType.PushPaneInput,
                          {
                            paneId: currentPane.id,
                            paneInput: {
                              id: generatePaneInputId(),
                              name: paneAutoInputName,
                              source: [p.id, 0],
                            },
                          },
                        ]);
                        view.dispatch({
                          changes: {
                            from: from,
                            to: to,
                            insert: paneAutoInputName,
                          },
                          selection: {
                            anchor: from + paneAutoInputName.length,
                          },
                        });
                      }
                    },
                    type: "import",
                    info:
                      infoContent !== undefined
                        ? (completion: Completion) => {
                            const div = document.createElement("div");
                            div.className = "max-h-20 overflow-hidden";
                            div.style.margin = "-6px";
                            div.innerHTML = `<div class="pl-1.5 pr-2 py-1 mb-1 border-b border-neutral-200">→ add pane input</div>`;
                            const contentDiv = document.createElement("div");
                            contentDiv.className =
                              "whitespace-pre-wrap text-neutral-400 px-1.5 pb-1";
                            contentDiv.innerText = infoContent;
                            div.appendChild(contentDiv);
                            return div;
                          }
                        : undefined,
                  });
                }
              }
            }
          }
        }

        let codeWorkerOptions: Completion[] = [];
        if (hasPeriodTokenBefore || context.explicit) {
          const triggerCharacter = !context.explicit ? "." : undefined;
          const expression = context.state.doc.toJSON().join("\n");
          // canvasAtom.value may not be updated with latest expression yet
          const code = convertPanesToTypeScriptCode(
            canvasAtom.value.panes.map((p) =>
              p.id !== paneId ? p : { ...p, expression }
            )
          );
          const needle = `/*${PANE_MARKER(paneId)}*/`;
          const expressionOffset = code.indexOf(needle) + needle.length;
          const pos = expressionOffset + context.pos;
          const response = await codeWorkerCall({
            type: "Autocomplete",
            code,
            pos,
            triggerCharacter,
          });

          if (response?.options?.length > 0) {
            const detailsCache: Map<
              string,
              CompletionEntryDetails | undefined
            > = new Map();
            async function completionInfo(completion: Completion) {
              let entryDetails: CompletionEntryDetails | undefined;
              if (detailsCache.has(completion.label)) {
                entryDetails = detailsCache.get(completion.label);
              } else {
                const response = await codeWorkerCall({
                  type: "AutocompleteInfo",
                  pos,
                  entryName: completion.label,
                });
                entryDetails = response?.entryDetails;
                detailsCache.set(completion.label, entryDetails);
              }
              const div = document.createElement("div");
              if (entryDetails !== undefined) {
                const displayText = entryDetails.displayParts
                  .map((part) => part.text)
                  .join("");
                div.innerHTML = displayText;
              }
              return div;
            }
            codeWorkerOptions = response.options.map(
              (completion: Completion) => ({
                ...completion,
                info: completionInfo,
              })
            );
            replacementSpanLength =
              response.optionalReplacementSpan !== undefined
                ? response.optionalReplacementSpan.length
                : 0;
          }
        }

        return {
          from: context.pos - replacementSpanLength,
          options: [...paneMatchOptions, ...codeWorkerOptions],
          validFor: /^\w*/,
        };
      },
    ],
  });
}

function LogValuesPanel({
  paneId,
  values,
  variableName,
  onClose,
}: {
  paneId: string;
  values: any[];
  variableName: string;
  onClose: () => void;
}) {
  return (
    <div className="h-full flex flex-col overflow-hidden relative">
      <div className="bg-neutral-100 p-2 shrink-0">
        <div className="text-neutral-500 mb-0.5">
          inspect {values.length} value
          {values.length !== 1 ? "s" : null}
        </div>
        <div className="font-mono-natto uppercase text-2xs text-primary-400">
          {modifierKey === "Meta" ? "⌘" : "ctrl"} click/drag value to interact
        </div>
      </div>
      <div className="grow overflow-auto">
        <LogInspectorValues
          paneId={paneId}
          values={values}
          variableName={variableName}
        />
      </div>
      <button
        onClick={() => onClose()}
        className="absolute top-1 right-1 flex p-1 text-neutral-400 hover:text-neutral-500 transition"
      >
        <FeatherIcon icon="x" size="md" />
      </button>
    </div>
  );
}

const LogValuesPanelWrapper = observer(
  ({
    paneId,
    inspectorLogValuesBox,
    cmSelectionIndexBox,
    panePortalElementBox,
  }: {
    paneId: string;
    inspectorLogValuesBox: IObservableValue<
      [from: number, to: number, name: string, values: any[]][] | undefined
    >;
    cmSelectionIndexBox: IObservableValue<number | undefined>;
    panePortalElementBox: IObservableValue<HTMLElement | null>;
  }) => {
    const selectedInspectorEntry = computed(() => {
      const inspectorLogValues = inspectorLogValuesBox.get();
      const cmSelectionIndex = cmSelectionIndexBox.get();
      if (inspectorLogValues === undefined || cmSelectionIndex === undefined) {
        return undefined;
      }
      return inspectorLogValues.find(
        (entry) => entry[0] <= cmSelectionIndex && entry[1] >= cmSelectionIndex
      );
    }).get();

    if (selectedInspectorEntry === undefined) {
      return null;
    }

    const panePortalElement = panePortalElementBox.get();
    if (panePortalElement === null) {
      return null;
    }
    return createPortal(
      <div
        className="log-values-popout absolute right-full rounded-l rounded-br top-[31px] bottom-0 w-[257px] bg-neutral-0 overflow-hidden border-r border-neutral-200 transition"
        key={selectedInspectorEntry[0]}
      >
        <LogValuesPanel
          paneId={paneId}
          values={selectedInspectorEntry[3]}
          variableName={selectedInspectorEntry[2]}
          onClose={action(() => {
            cmSelectionIndexBox.set(undefined);
          })}
        />
      </div>,
      panePortalElement
    );
  }
);

export function useCodemirrorVim(
  cmViewRef: React.RefObject<EditorView | undefined>
) {
  useEffect(() => {
    let isMounted = true;
    const cleanup = reaction(
      () => appSettingsMobx.vimEnabled,
      async (isVimEnabled) => {
        const cmView = cmViewRef.current!;
        if (isVimEnabled) {
          const codemirrorVim = await import("@replit/codemirror-vim");
          if (isMounted) {
            cmView.dispatch({
              effects: vimCompartment.reconfigure([
                codemirrorVim.vim(),
                drawSelection(),
              ]),
            });
          }
        } else {
          cmView.dispatch({
            effects: vimCompartment.reconfigure([]),
          });
        }
      },
      { fireImmediately: true }
    );
    return () => {
      isMounted = false;
      cleanup();
    };
  }, []);
}

export const PaneExpressionEditor = forwardRef(
  (
    {
      paneId,
      paneComputed,
      getInitialValue,
      placeholder,
      onChange,
      onAddParensForLoopWithAutorun,
      focusOnMount,
      toggleMaximize,
      inspectorLogValuesBox,
      expressionType,
      setIsEditorFocused,
      isStateTypePane = false,
      panePortalElementBox,
    }: {
      paneId: string;
      paneComputed: IComputedValue<Pane | undefined>;
      getInitialValue: () => PaneExpression;
      placeholder?: string;
      onChange?: (expression: PaneExpression) => void;
      onAddParensForLoopWithAutorun?: () => void;
      focusOnMount: boolean;
      toggleMaximize: (action: any) => void;
      inspectorLogValuesBox?: IObservableValue<
        [from: number, to: number, name: string, values: any[]][] | undefined
      >;
      expressionType: EvaluateExpressionType;
      setIsEditorFocused?: React.Dispatch<React.SetStateAction<boolean>>;
      isStateTypePane?: boolean;
      panePortalElementBox?: IObservableValue<HTMLElement | null>;
    },
    ref
  ) => {
    const rootRef = useRef<HTMLDivElement | null>(null);
    const tabFocusRef = useRef<HTMLDivElement | null>(null);
    const cmViewRef = useRef<
      (EditorView & { expressionType: EvaluateExpressionType }) | undefined
    >();
    const [cmSelectionIndexBox] = useState(() =>
      observable.box<number | undefined>(undefined, { deep: false })
    );
    const [logValuesPopper, setLogValuesPopper] = useState<
      [from: number, to: number, name: string, values: any[]] | undefined
    >(undefined);

    const onDragLogInspectorCleanupRef = useRef<(() => void) | undefined>(
      undefined
    );
    useEffect(() => {
      return () => {
        if (onDragLogInspectorCleanupRef.current !== undefined) {
          onDragLogInspectorCleanupRef.current();
        }
      };
    }, []);

    const useLanguageExtensions =
      expressionType !== EvaluateExpressionType.Text;
    useEffect(() => {
      const initialValue = getInitialValue();
      let docValue = initialValue;
      const extensions = [
        vimCompartment.of([]),
        ...cmExtensions,
        languageCompartment.of([]),
      ];
      if (!isStateTypePane) {
        extensions.push(
          ViewPlugin.fromClass(
            class {
              update(update: ViewUpdate) {
                if (!update.changes.empty) {
                  const isRemote = update.transactions.some((transaction) =>
                    transaction.annotation(Transaction.remote)
                  );
                  if (!isRemote && !update.changes.empty) {
                    canvasDispatch([
                      ActionType.UpdatePaneCodemirror,
                      {
                        paneId,
                        v: applicationState.server?.[1] ?? 0,
                        f: "expression",
                        changes: update.changes.toJSON(),
                      },
                    ]);
                  }
                }
              }
            }
          ),
          presenceDispatchPlugin(paneId)
        );
      }
      const cmView = new EditorView({
        state: EditorState.create({
          doc: initialValue,
          extensions:
            placeholder !== undefined
              ? [...extensions, cmPlaceholder(placeholder)]
              : extensions,
        }),
        dispatch: (transaction) => {
          cmView.update([transaction]);
          const newValue = cmView.state.doc.toJSON().join("\n");
          if (
            transaction.annotation(Transaction.remote) !== true &&
            docValue !== newValue
          ) {
            const pane = paneComputed.get();
            if (
              pane?.type === PaneType.Evaluate &&
              pane.expressionType !== EvaluateExpressionType.Text &&
              pane.autorun
            ) {
              // detect whether the new change adds () to a for/while loop
              let didJustAddParensForLoop = false;
              transaction.changes.iterChanges(
                (fromA, toA, fromB, toB, inserted) => {
                  for (const line of inserted) {
                    if (line === "()" || line === "(") {
                      const tree = syntaxTree(cmView.state);
                      const node = tree.resolveInner(toB, -1);
                      if (
                        node.parent?.parent?.type.name === "ForSpec" ||
                        node.parent?.parent?.parent?.type.name ===
                          "WhileStatement"
                      ) {
                        didJustAddParensForLoop = true;
                      }
                    }
                  }
                }
              );
              if (didJustAddParensForLoop) {
                onAddParensForLoopWithAutorun?.();
              }
            }
            onChange?.(newValue);
          }
          docValue = newValue;
          if (
            transaction.selection?.ranges?.length === 1 &&
            transaction.selection.ranges[0].empty
          ) {
            const selectionIndex = transaction.selection.ranges[0].anchor;
            runInAction(() => {
              cmSelectionIndexBox.set(selectionIndex);
            });
          } else {
            runInAction(() => {
              cmSelectionIndexBox.set(undefined);
            });
          }
        },
        parent: rootRef.current!,
      });
      function onKeyPress(e: KeyboardEvent) {
        if (e.ctrlKey && e.key === "M") {
          toggleMaximize("editor keyboard shortcut");
        } else if (["(", ")", "{", "}", " "].includes(e.key)) {
          closeCompletion(cmView);
        } else if (e.shiftKey && e.key === "Enter") {
          EvaluatorRunEmitter.emit(`${paneId}`);
          e.preventDefault();
        }
      }
      cmView.contentDOM.addEventListener("keypress", onKeyPress);
      function onKeyDown(e: KeyboardEvent) {
        if (
          ["KeyK", "KeyP"].includes(e.code) &&
          isSecondaryModifierKeyDown(e.metaKey, e.altKey)
        ) {
          toggleCommandBar();
        }
      }
      cmView.contentDOM.addEventListener("keydown", onKeyDown);
      function onBlur(e: FocusEvent) {
        closeCompletion(cmView);
      }
      cmView.contentDOM.addEventListener("blur", onBlur);
      // TODO: convert this to useDrag and remove onDragNewPaneMouseDown
      function onMouseDown(e: MouseEvent) {
        if (
          inspectorLogValuesBox !== undefined &&
          isModifierKeyDown(e.metaKey, e.ctrlKey) &&
          e.target instanceof HTMLElement
        ) {
          const logValueElement = getParentByClassName(
            e.target,
            LOG_VALUE_TOKEN_CLASSNAME
          );
          if (logValueElement !== undefined) {
            const from = parseInt(
              logValueElement.getAttribute("data-from") ?? "-1"
            );
            const to = parseInt(
              logValueElement.getAttribute("data-to") ?? "-1"
            );
            const values = inspectorLogValuesBox
              .get()
              ?.find((entry) => entry[0] === from && entry[1] === to)?.[3];
            if (values !== undefined) {
              onDragNewPaneMouseDown(
                e,
                action((position) => {
                  dragValueAtom.value = {
                    type: "value",
                    name: logValueElement.textContent!,
                    value: values,
                    position,
                  };
                }),
                onDragLogInspectorCleanupRef,
                () => {},
                true
              );
            }
          }
        }
      }
      cmView.contentDOM.addEventListener("mousedown", onMouseDown);
      // @ts-ignore
      cmViewRef.current = cmView;
      if (focusOnMount) {
        cmView.focus();
        // ideally this is set in EditorState.create but doesn't seem to work.
        setTimeout(() => {
          cmView.dispatch({
            selection: EditorSelection.single(initialValue.length),
          });
        }, 0);
      }

      function onRemoteUpdate({
        paneField,
        changes,
      }: {
        paneField: "expression" | "text";
        changes: ["changes", ChangeSet] | ["value", string];
      }) {
        if (paneField === "expression") {
          const newState = cmView.state.update({
            changes:
              changes[0] === "changes"
                ? changes[1]
                : {
                    from: 0,
                    to: cmView.state.doc.length,
                    insert: changes[1],
                  },
            annotations: [Transaction.remote.of(true)],
          });
          cmView.dispatch(newState);
        }
      }
      PaneEditorRemoteUpdateEmitter.addListener(paneId, onRemoteUpdate);

      const unsub = !isStateTypePane
        ? subscribeCodemirrorPaneId(paneId, cmView)
        : () => {};

      return () => {
        PaneEditorRemoteUpdateEmitter.removeListener(paneId, onRemoteUpdate);
        cmView.contentDOM.removeEventListener("keypress", onKeyPress);
        cmView.contentDOM.removeEventListener("keydown", onKeyDown);
        cmView.contentDOM.removeEventListener("blur", onBlur);
        cmView.contentDOM.removeEventListener("mousedown", onMouseDown);
        cmView.destroy();
        unsub();
      };
    }, []);
    useEffect(() => {
      cmViewRef.current!.expressionType = expressionType;
    }, [expressionType]);
    useEffect(() => {
      cmViewRef.current!.dispatch({
        effects: languageCompartment.reconfigure(
          useLanguageExtensions
            ? [
                ...cmLanguageExtensions,
                createAutocompleteExtension(paneId),
                keymap.of([
                  {
                    key: "Alt-F",
                    run: (editorView) => {
                      formatEditorCode(editorView);
                      return true;
                    },
                  },
                ]),
              ]
            : []
        ),
      });
    }, [useLanguageExtensions]);
    useCodemirrorVim(cmViewRef);
    useEffect(
      () =>
        inspectorLogValuesBox !== undefined
          ? reaction(
              () => inspectorLogValuesBox.get(),
              (inspectorLogValues) => {
                if (cmViewRef.current !== undefined) {
                  cmViewRef.current.dispatch({
                    effects: [setLogValuesEffect.of(inspectorLogValues)],
                  });
                }
              }
            )
          : undefined,
      []
    );

    useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          cmViewRef.current?.focus();
        },
        formatCode: () => {
          const cmView = cmViewRef.current;
          if (cmView !== undefined) {
            formatEditorCode(cmView);
          }
        },
        wrapSelectionInCanvasPortal: (
          paneRect: Rect,
          libraryInputName: string
        ) => {
          const cmView = cmViewRef.current;
          if (cmView !== undefined) {
            wrapSelectionInCanvasPortal(cmView, paneRect, libraryInputName);
          }
        },
        setCode: (code: string) => {
          const cmView = cmViewRef.current;
          if (cmView !== undefined) {
            cmView.dispatch({
              changes: {
                from: 0,
                to: cmView.state.doc.length,
                insert: code,
              },
            });
          }
        },
        setExpressionRemote: (expression: string) => {
          const cmView = cmViewRef.current!;
          if (!cmView.hasFocus) {
            cmView.dispatch({
              changes: {
                from: 0,
                to: cmView.state.doc.length,
                insert: expression,
              },
              annotations: [Transaction.remote.of(true)],
            });
          }
        },
        extractPaneName: () => {
          const cmView = cmViewRef.current;
          if (cmView !== undefined) {
            return extractPaneName(cmView);
          }
        },
        renameInput: async (paneInputId: string, newInputName: string) => {
          const cmView = cmViewRef.current;
          if (cmView !== undefined) {
            await renameInput(cmView, paneId, paneInputId, newInputName);
          }
        },
      }),
      []
    );

    return (
      <>
        <div
          onKeyDown={(e) => {
            if (e.target === rootRef.current!) {
              switch (e.key) {
                case "Tab": {
                  if (!e.shiftKey) {
                    tabFocusRef.current!.focus();
                  }
                  break;
                }
                case "Enter": {
                  if (!e.ctrlKey && !e.metaKey && !e.shiftKey) {
                    cmViewRef.current?.focus();
                    e.preventDefault();
                  }
                  break;
                }
              }
            }
          }}
          onFocus={() => {
            if (setIsEditorFocused !== undefined) {
              setIsEditorFocused(true);
            }
          }}
          onBlur={() => {
            if (setIsEditorFocused !== undefined) {
              setIsEditorFocused(false);
            }
          }}
          className="h-full select-text focus-visible-inset"
          ref={rootRef}
          tabIndex={-1}
        />
        <div ref={tabFocusRef} tabIndex={-1} />
        {inspectorLogValuesBox !== undefined ? (
          <LogValuesPanelWrapper
            paneId={paneId}
            inspectorLogValuesBox={inspectorLogValuesBox}
            cmSelectionIndexBox={cmSelectionIndexBox}
            panePortalElementBox={panePortalElementBox!}
          />
        ) : null}
      </>
    );
  }
);
